import { StyledLayout } from '$globalStyledComponents/Layout'
import { StyledButton } from '$globalStyledComponents/Buttons'
import Footer from '$components/Footer'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useMemo } from 'react'
import {
  parseMaterialFilterFromUrl,
  parseMaterialNameFromUrl,
} from '$utils/deepLinkUtils'
import Topwelcomecard from './Topwelcomecard'
//import Editgroup from './Editgroup'
import Tabcontent from './Tabcontent'
import { Row, Col, Button, Popconfirm, Modal, message } from 'antd'
import { StyledGroupTabs } from './styledComponents'
import { useRef, useState, useEffect } from 'react'
import { PlusOutlined } from '@ant-design/icons'
import {
  fetchAllGroups,
  postGroup,
  putGroup,
  deleteGroup,
  fetchParts,
  putPartsToGroup,
} from '$api/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FcUpdateParts } from '$utils/FcUpdateParts'
import { useRefetchTimer } from '$context/RefetchTimerProvider'
import GroupConfigModal from './GroupConfigModal'

import AllPartsTable from './AllPartsTable'

const { Content } = StyledLayout

const COLUMNS = [
  'feasible',
  'part_id',
  'name',
  'width',
  'depth',
  'height',
  'weight',
  'material',
  // 'client_material_id',
  'price',
  'price_am',
  'created_at',
  'updated_at',
]

const Partsmasterdata = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { startRefetch } = useRefetchTimer()
  const [searchParams, setSearchParams] = useSearchParams()
  const [partsCount, setPartsCount] = useState(0)
  const [groupState, setGroupState] = useState([
    {
      tabId: 1,
      isNotSaved: false,
      partsCount: undefined,
    },
  ])

  const defaultPanes = useMemo(
    () => [
      {
        label: t('All Parts'),
        children: (
          <AllPartsTable
            columns={COLUMNS}
            setPartsTotal={(total) => setPartsCount(total)}
          />
        ),
        key: '1',
        disabled: false,
      },
    ],
    [t],
  )

  const [activeKey, setActiveKey] = useState(defaultPanes[0].key)
  const [items, setItems] = useState(defaultPanes)
  const [groupPartsLength, setGroupPartsLength] = useState(0)
  const newTabIndex = useRef(0)
  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [groupFilterDescription, setGroupFilterDescription] = useState('')
  const [_listPartIDs, setListPartIDs] = useState([])
  const [addNewGroup, setAddNewGroup] = useState(false)
  const [isChanged, setIsChanged] = useState(false)
  const [isChangedPlusButton, setIsChangedPlusButton] = useState(false)
  const [filteredPartsIds, setFilteredPartsIds] = useState([])
  const [isGroupConfigModalOpen, setIsGroupConfigModalOpen] = useState(false)
  const [groupConfigLoading, setGroupConfigLoading] = useState(false)

  //react queries
  const { data: groupsData } = useQuery(['allgroups'], fetchAllGroups)
  const newGroupMutation = useMutation(postGroup, {
    onSuccess: (response) => {
      const newGroupId = response.data
      const tempKey = activeKey
      setAddNewGroup(false)

      setItems((prevItems) => prevItems.filter((item) => item.key !== tempKey))
      setGroupState((prevState) =>
        prevState.filter((item) => item.tabId !== tempKey),
      )

      if (newGroupId) {
        setActiveKey(newGroupId.toString())
      } else {
        setActiveKey('1')
      }
      message.success(t('parts_master_data.group_created'))

      queryClient.invalidateQueries(['allgroups'])
    },
    onError: (error) => {
      console.info(error)
    },
  })

  const materialNameFromUrl = useMemo(() => {
    return parseMaterialNameFromUrl(searchParams)
  }, [searchParams])

  const materialFilterFromUrl = useMemo(() => {
    const materialId = parseMaterialFilterFromUrl(searchParams)
    if (materialId) {
      return {
        client_material_id: [
          {
            parameter: 'client_material_id',
            logical_operator: '=',
            value: materialId,
          },
        ],
      }
    }
    return null
  }, [searchParams])

  const queryFilters = {
    pageNr: 1,
    pageSize: 10000,
    sorting: {},
    filters: materialFilterFromUrl || {},
    groupId: null,
  }

  const { isSuccess: isFilteredPartsSuccess } = useQuery({
    queryKey: ['filteredParts', queryFilters],
    queryFn: fetchParts,
    enabled: !!materialFilterFromUrl,
    onSuccess: (data) => {
      setFilteredPartsIds(data.items.map((part) => part.id))
    },
  })

  const addToGroupMutation = useMutation({
    mutationFn: putPartsToGroup,
    onSuccess: () => {
      queryClient.refetchQueries(['allpartspaginated'])
      queryClient.invalidateQueries(['parts'])
    },
    onError: (err) => {
      console.error(err)
    },
  })

  const createGroupForMaterial = () => {
    if (!materialNameFromUrl) return

    isFilteredPartsSuccess && setIsGroupConfigModalOpen(true)
    // Check if group with this name already exists
    if (
      groupsData &&
      groupsData.some((group) => group.list_name === materialNameFromUrl)
    ) {
      // Find existing group and activate it
      const existingGroup = groupsData.find(
        (group) => group.list_name === materialNameFromUrl,
      )
      setActiveKey(existingGroup.id.toString())

      return
    }

    setSearchParams({}, { replace: true })
  }

  useEffect(() => {
    if (
      materialNameFromUrl &&
      groupsData &&
      isFilteredPartsSuccess &&
      filteredPartsIds.length > 0
    ) {
      createGroupForMaterial()
    }
  }, [
    materialNameFromUrl,
    groupsData,
    isFilteredPartsSuccess,
    filteredPartsIds,
  ])

  const handleGroupConfigOk = async (values) => {
    setGroupConfigLoading(true)
    if (values.groupType === 'new') {
      if (
        groupsData &&
        groupsData.some(
          (group) =>
            group.list_name.trim().toLowerCase() ===
            values.groupName.trim().toLowerCase(),
        )
      ) {
        message.error(t('parts_master_data.group_name_exists'))
        setGroupConfigLoading(false)
        return
      }

      const body = {
        list_name: values.groupName,
        description: `Group created for material: ${values.groupName}`,
        filter_description: '',
      }

      await newGroupMutation.mutateAsync(body, {
        onSuccess: (response) => {
          addToGroupMutation.mutate({
            partsIds: filteredPartsIds,
            groupId: response.data,
          })
          setActiveKey(response.data.toString())
          setIsGroupConfigModalOpen(false)
          setGroupConfigLoading(false)
          setSearchParams({}, { replace: true })
        },
        onError: () => setGroupConfigLoading(false),
      })
    } else {
      addToGroupMutation.mutate(
        {
          partsIds: filteredPartsIds,
          groupId: values.existingGroupId,
        },
        {
          onSuccess: () => {
            setActiveKey(values.existingGroupId.toString())
            setIsGroupConfigModalOpen(false)
            setGroupConfigLoading(false)
            setSearchParams({}, { replace: true })
            message.success(t('parts_master_data.group_updated'))
            queryClient.invalidateQueries(['allgroups'])
          },
          onError: () => setGroupConfigLoading(false),
        },
      )
    }
  }

  function searchGroupName(nameKey, myArray) {
    for (var i = 0; i < myArray.length; i++) {
      if (myArray[i].key === nameKey) {
        return i
      }
    }
  }

  const updateGroupMutation = useMutation(putGroup, {
    onSuccess: () => {
      setAddNewGroup(false)
      message.success(t('parts_master_data.group_updated'))
      queryClient.invalidateQueries(['allgroups'])
    },
  })
  const deleteGroupMutation = useMutation(deleteGroup, {
    onSuccess: () => {
      queryClient.invalidateQueries(['allgroups'])
    },
    onError: (e) => {
      console.error(e)
      queryClient.resetQueries(['allgroups'])
    },
  })

  const onChange = (key) => {
    if (key) {
      setActiveKey(key)
    }
  }

  useEffect(() => {
    if (activeKey !== '1' && groupsData) {
      if (
        groupsData &&
        groupsData[groupsData.map((e) => e.id).indexOf(activeKey)]?.total_parts
      ) {
        setGroupPartsLength(
          groupsData[groupsData.map((e) => e.id).indexOf(activeKey)]
            ?.total_parts,
        )
      } else if (activeKey.includes('T')) {
        const index = groupState.findIndex((e) => e.tabId === activeKey)
        if (index !== -1) {
          setGroupState((prevState) => {
            const newState = [...prevState]
            newState[index] = {
              ...newState[index],
              partsCount: groupPartsLength,
            }
            return newState
          })
        }
      }
    } else {
      setGroupPartsLength('-')
    }
  }, [activeKey, groupsData])

  useEffect(() => {
    if (activeKey.includes('T')) {
      const index = groupState.findIndex((e) => e.tabId === activeKey)
      if (index !== -1) {
        setGroupState((prevState) => {
          const newState = [...prevState]
          newState[index] = {
            ...newState[index],
            partsCount: groupPartsLength,
          }
          return newState
        })
      }
    }
  }, [groupPartsLength, activeKey])

  useEffect(() => {
    if (activeKey !== items[0]?.key && items.length > 0) {
      const newPanes2 = [...items]
      if (isChanged && items.length > 0) {
        const groupIndex = searchGroupName(activeKey, items)
        if (groupIndex !== undefined && newPanes2[groupIndex]) {
          newPanes2[groupIndex].label = groupName
        }
      }
      setItems(newPanes2)
    }
  }, [groupName])

  useEffect(() => {
    if (isChanged) {
      setItems((prevItems) =>
        prevItems.map((item) => ({ ...item, disabled: true })),
      )
      setIsChangedPlusButton(true)
    }
  }, [isChanged])

  useEffect(() => {
    const newGroupState = [
      {
        tabId: 1,
        isNotSaved: false,
        partsCount: undefined,
      },
    ]

    const newPanes = (groupsData || []).map((group) => {
      const id = group.id
      newGroupState.push({
        tabId: id,
        isNotSaved: false,
        partsCount: group.total_parts,
      })
      return {
        label: group.list_name,
        children: (
          <Tabcontent
            groupName={group.list_name}
            setGroupName={setGroupName}
            groupDescription={group.description}
            setGroupDescription={setGroupDescription}
            filterDescription={group.filter_description}
            setGroupFilterDescription={setGroupFilterDescription}
            setPartsCount={setPartsCount}
            setIsChanged={setIsChanged}
            setListPartIDs={setListPartIDs}
            setGroupPartsLength={setGroupPartsLength}
            addNewGroup={false}
            groupId={group.id}
            columns={COLUMNS}
          />
        ),
        key: id,
        disabled: false,
      }
    })

    newPanes.unshift(defaultPanes[0])
    setItems(newPanes)
    setGroupState(newGroupState)
    queryClient.removeQueries(['groupparts', null])
  }, [groupsData, defaultPanes])

  useEffect(() => {
    if (localStorage.getItem('notupdatedfc')) {
      let timeoutId

      startRefetch(
        5000,
        () => {
          if (timeoutId) clearTimeout(timeoutId)

          timeoutId = setTimeout(() => {
            queryClient.refetchQueries(['feasibilitycheck'])
          }, 500)
        },
        0,
      )
      return () => {
        if (timeoutId) clearTimeout(timeoutId)
      }
    }
  }, [])

  const onSave = () => {
    if (!groupName) {
      info()
      return
    }

    setItems((prevItems) =>
      prevItems.map((item) => ({ ...item, disabled: false })),
    )
    setIsChangedPlusButton(false)

    // set groupName when not touching "editparts" only doing things in "masterdatatable"
    let body = {
      list_name: groupName,
      description: groupDescription,
      filter_description: groupFilterDescription,
    }
    if (addNewGroup) {
      newGroupMutation.mutate(body)
    } else {
      updateGroupMutation.mutate({
        ...body,
        groupId: activeKey,
      })
    }
  }

  const onDelete = (targetKey) => {
    remove(targetKey)
    setIsChangedPlusButton(false)
    if (!addNewGroup) {
      deleteGroupMutation.mutate(activeKey)
    } else {
      queryClient.resetQueries(['allgroups'])
    }
    setActiveKey(defaultPanes[0].key)
  }

  const add = () => {
    setIsChangedPlusButton(true)
    setAddNewGroup(true)
    setGroupDescription('')
    const newActiveKey = `T${newTabIndex.current++}`
    setGroupFilterDescription('')
    const newGroupName = `${t('parts_master_data.new_group')} ${newTabIndex.current}`
    setGroupName(newGroupName)

    setItems((prevItems) =>
      prevItems.map((item) => ({ ...item, disabled: true })),
    )

    setGroupState((prevState) => [
      ...prevState,
      {
        tabId: newActiveKey,
        isNotSaved: true,
        partsCount: 0,
      },
    ])

    setItems((prevItems) => [
      ...prevItems,
      {
        label: newGroupName,
        children: (
          <Tabcontent
            groupName={newGroupName}
            setGroupName={setGroupName}
            groupDescription={''}
            setGroupDescription={setGroupDescription}
            filterDescription={''}
            setGroupFilterDescription={setGroupFilterDescription}
            setPartsCount={setPartsCount}
            setIsChanged={setIsChanged}
            setListPartIDs={setListPartIDs}
            setGroupPartsLength={setGroupPartsLength}
            addNewGroup={false}
            groupId={undefined}
            columns={COLUMNS}
          />
        ),
        key: newActiveKey,
        disabled: false,
      },
    ])
    setActiveKey(newActiveKey)
    setIsChanged(true)
  }

  const remove = (targetKey) => {
    const targetIndex = items.findIndex((pane) => pane.key === targetKey)
    const newPanes = items.filter((pane) => pane.key !== targetKey)
    if (newPanes.length && targetKey === activeKey) {
      const { key } =
        newPanes[
          targetIndex === newPanes.length ? targetIndex - 1 : targetIndex
        ]
      setActiveKey(key)
    }
    setItems(newPanes)

    setGroupState((prevState) =>
      prevState.filter((item) => item.tabId !== targetKey),
    )
  }

  const onEdit = (targetKey, action) => {
    if (action === 'add') {
      add()
    } else {
      remove(targetKey)
    }
  }
  const info = () => {
    Modal.info({
      title: t('parts_master_data.delete_group_confirm'),
      content: (
        <div>
          <p>{t('parts_master_data.name_required')}</p>
        </div>
      ),
      onOk() {},
    })
  }

  return (
    <StyledLayout className="layout">
      <Content>
        <Row style={{ margin: '46px 0' }}>
          <Col flex={5} style={{ margin: 'auto' }}>
            <h1 style={{ margin: '0px 0' }}>{t('parts_master_data.title')}</h1>
          </Col>
          <Col style={{ marginRight: '25px' }} flex={1}></Col>
        </Row>
        <Topwelcomecard
          groupsCount={items.length - 1}
          isGroup={activeKey !== '1'}
          activeGroup={activeKey}
          currentGroupPartsCount={groupPartsLength}
          partsCount={partsCount}
          groups={groupsData}
        />
        <div className="partsmasterdatatablecard">
          <StyledGroupTabs
            animated
            hideAdd
            tabBarExtraContent={{
              left: (
                <StyledButton onClick={add} disabled={isChangedPlusButton}>
                  <PlusOutlined />
                </StyledButton>
              ),
            }}
            onChange={onChange}
            activeKey={activeKey}
            type="card"
            onEdit={onEdit}
            items={items}
          />
          {addNewGroup ? (
            <div style={{ textAlign: 'right' }}>
              <Button
                type="secondary"
                style={{ margin: '15px' }}
                onClick={() => {
                  onDelete()
                  setAddNewGroup(false)
                }}
              >
                {t('button.cancel')}
              </Button>
              <Button
                type="primary"
                style={{ margin: '15px' }}
                onClick={onSave}
                disabled={groupName === ''}
              >
                {t('parts_master_data.add_group')}
              </Button>
            </div>
          ) : activeKey !== '1' ? (
            <div style={{ textAlign: 'right' }}>
              <Popconfirm
                title={t('parts_master_data.delete_group_confirm')}
                okText={t('Yes')}
                cancelText={t('No')}
                onConfirm={onDelete}
              >
                <Button type="secondary" style={{ margin: '15px 0' }}>
                  {t('parts_master_data.delete_group')}
                </Button>
              </Popconfirm>
              <Button
                type="primary"
                style={{ margin: '15px' }}
                onClick={onSave}
                disabled={!isChanged}
              >
                {t('parts_master_data.save_group')}
              </Button>
            </div>
          ) : (
            ''
          )}
        </div>
        <FcUpdateParts />
      </Content>
      <Footer />
      <GroupConfigModal
        open={isGroupConfigModalOpen}
        onCancel={() => setIsGroupConfigModalOpen(false)}
        onOk={handleGroupConfigOk}
        groups={groupsData}
        defaultGroupName={materialNameFromUrl}
        loading={groupConfigLoading}
      />
    </StyledLayout>
  )
}

export default Partsmasterdata
